import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
    BookmarkSimple,
    ChatCircle,
    Eye,
    Flag,
    Heart,
    MapPin,
    PaperPlaneTilt,
    Pause,
    Play,
    Plus,
    SpeakerHigh,
    SpeakerSlash,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { reelsApi, toAbsolute } from "@/lib/apiClient";
import MediaUploader from "@/components/MediaUploader";
import FollowButton from "@/components/FollowButton";
import ReelCommentsDrawer from "@/components/ReelCommentsDrawer";
import ReelModal from "@/components/ReelModal";
import { ReportModal } from "@/components/PostCard";
import { useAuth } from "@/context/AuthContext";
import { useTranslation } from "react-i18next";
import { DeleteReelDialog, EditReelModal, ReelOptionsMenu, ReelUnavailableState, getReelDeleteErrorMessage } from "@/components/ReelManagement";
import { usePassportShare } from "@/components/messages/PassportShareProvider";

const MODES = [
    { key: "for_you", label: "For You" },
    { key: "following", label: "Following" },
    { key: "trending", label: "Trending" },
];

function getSessionId() {
    const key = "passport_reels_session_id";
    const existing = localStorage.getItem(key);
    if (existing) return existing;
    const next = `reel-${Math.random().toString(36).slice(2, 11)}`;
    localStorage.setItem(key, next);
    return next;
}

function timeAgo(iso) {
    if (!iso) return "";
    const seconds = Math.round((Date.now() - new Date(iso).getTime()) / 1000);
    if (seconds < 60) return `${seconds}s ago`;
    const minutes = Math.round(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.round(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    return `${Math.round(hours / 24)}d ago`;
}

async function copyShareUrl(text) {
    if (navigator.clipboard?.writeText) {
        try {
            await navigator.clipboard.writeText(text);
            return true;
        } catch {
            // Fall through to legacy copy.
        }
    }
    try {
        const input = document.createElement("textarea");
        input.value = text;
        input.setAttribute("readonly", "true");
        input.style.position = "absolute";
        input.style.left = "-9999px";
        document.body.appendChild(input);
        input.select();
        const copied = document.execCommand("copy");
        document.body.removeChild(input);
        return copied;
    } catch {
        return false;
    }
}

export default function Reels({ onReelCreated }) {
    const { user } = useAuth() || {};
    const { openPassportShare } = usePassportShare();
    const { t } = useTranslation();
    const [searchParams, setSearchParams] = useSearchParams();
    const reelQueryId = searchParams.get("reel");
    const [mode, setMode] = useState("for_you");
    const [reels, setReels] = useState([]);
    const [cursor, setCursor] = useState(null);
    const [hasMore, setHasMore] = useState(true);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [showCreate, setShowCreate] = useState(false);
    const [commentReel, setCommentReel] = useState(null);
    const [reportReel, setReportReel] = useState(null);
    const [activeReel, setActiveReel] = useState(null); // For modal viewer
    const [unavailableReel, setUnavailableReel] = useState(null);
    const sentinelRef = useRef(null);

    const updateReel = useCallback((reelId, updater) => {
        setReels((prev) => prev.map((item) => {
            if (item.id !== reelId) return item;
            return typeof updater === "function" ? updater(item) : { ...item, ...updater };
        }));
        setActiveReel((prev) => {
            if (!prev || prev.id !== reelId) return prev;
            return typeof updater === "function" ? updater(prev) : { ...prev, ...updater };
        });
    }, []);

    const fetchReels = useCallback(async (nextCursor = null, reset = false) => {
        if ((reset && loading) || (!reset && (loadingMore || !hasMore))) return;
        reset ? setLoading(true) : setLoadingMore(true);
        try {
            const response = await reelsApi.list({ mode, limit: 6, ...(nextCursor ? { cursor: nextCursor } : {}) });
            const items = response.items || [];
            setReels((prev) => (reset ? items : [...prev, ...items.filter((item) => !prev.some((entry) => entry.id === item.id))]));
            setCursor(response.next_cursor || null);
            setHasMore(Boolean(response.next_cursor));
        } catch {
            if (reset) setReels([]);
            setHasMore(false);
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    }, [mode, loading, loadingMore, hasMore]);

    useEffect(() => {
        setCursor(null);
        setHasMore(true);
        fetchReels(null, true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode]);

    useEffect(() => {
        if (!reelQueryId) return;
        reelsApi.get(reelQueryId).then((item) => {
            setActiveReel(item);
            setUnavailableReel(null);
        }).catch((error) => {
            const status = error?.response?.status;
            setUnavailableReel({
                status,
                title: status === 404 ? "Reel no longer available." : "This reel can't be opened right now.",
                description: status === 404
                    ? "The Reel you followed has been removed or deleted."
                    : "Try another Reel from the Travel Feed or sign in if this reel is private.",
            });
        });
    }, [reelQueryId]);

    // Infinite scroll sentinel
    useEffect(() => {
        const el = sentinelRef.current;
        if (!el) return;
        const observer = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting && hasMore && !loading && !loadingMore) {
                fetchReels(cursor);
            }
        }, { threshold: 0.1 });
        observer.observe(el);
        return () => observer.disconnect();
    }, [cursor, hasMore, loading, loadingMore, fetchReels]);

    useEffect(() => {
        if (!reelQueryId) setUnavailableReel(null);
    }, [reelQueryId]);


    const handleLike = async (reel) => {
        if (!user) return toast.error("Sign in to like reels");
        const previousLiked = reel.liked;
        updateReel(reel.id, { liked: !previousLiked, likes: Math.max((reel.likes || 0) + (previousLiked ? -1 : 1), 0) });
        try {
            const response = await reelsApi.like(reel.id);
            updateReel(reel.id, { liked: response.liked, likes: response.likes ?? reel.likes });
        } catch {
            updateReel(reel.id, { liked: previousLiked, likes: reel.likes || 0 });
            toast.error("Could not update reel");
        }
    };

    const handleSave = async (reel) => {
        if (!user) return toast.error("Sign in to save reels");
        try {
            if (reel.saved) {
                await reelsApi.unsave(reel.id);
                updateReel(reel.id, { saved: false, saves: Math.max((reel.saves || 1) - 1, 0) });
                toast.success("Removed from saved");
            } else {
                await reelsApi.save(reel.id);
                updateReel(reel.id, { saved: true, saves: (reel.saves || 0) + 1 });
                toast.success("Saved to your Passport");
            }
        } catch {
            toast.error("Could not update saved reels");
        }
    };

    const handleShare = async (reel) => {
        try {
            const response = await reelsApi.share(reel.id);
            const url = `${window.location.origin}${response.path}`;
            let shared = false;
            if (navigator.share) {
                try {
                    await navigator.share({ title: "Passport Reel", text: reel.caption, url });
                    shared = true;
                } catch {
                    shared = false;
                }
            }
            if (!shared) {
                const copied = await copyShareUrl(url);
                toast.success(copied ? "Reel link copied" : "Share link ready");
            }
            updateReel(reel.id, { shares: (reel.shares || 0) + 1 });
        } catch {
            toast.error("Could not share reel");
        }
    };

    const handleShareInMessages = (reel) => {
        openPassportShare({ shareRef: { entity_type: "reel", entity_id: reel.id }, sourceLabel: "this Reel" });
    };



    return (
        <div className="space-y-5" data-testid="reels-panel">
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
                <div>
                    <div className="overline mb-1">{t("reels.overline")}</div>
                    <div className="font-serif text-4xl md:text-5xl text-white leading-[0.95] tracking-tight" data-testid="reels-title">{t("reels.title")}</div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <div className="flex gap-1 p-1 rounded-full" style={{ background: "rgba(0,0,0,0.28)", border: "1px solid rgba(255,255,255,0.08)" }} data-testid="reel-mode-group">
                        {MODES.map((item) => (
                            <button
                                key={item.key}
                                onClick={() => setMode(item.key)}
                                className="px-4 py-2 rounded-full text-xs transition-colors"
                                style={{ background: mode === item.key ? "var(--gold)" : "transparent", color: mode === item.key ? "var(--navy)" : "rgba(255,255,255,0.78)" }}
                                data-testid={`reel-mode-${item.key}`}
                            >
                                {item.label}
                            </button>
                        ))}
                    </div>
                    <button onClick={() => setShowCreate(true)} className="btn-gold text-sm !py-2.5 !px-5" data-testid="reels-add">
                        <Plus size={14} weight="bold" /> {t("reels.newReel")}
                    </button>
                </div>
            </div>

            {unavailableReel && reelQueryId && (
                <ReelUnavailableState title={unavailableReel.title} description={unavailableReel.description} onBack={() => setSearchParams({ tab: "feed" })} />
            )}

            {reels.length === 0 && !loading && (
                <div className="rounded-3xl px-8 py-16 text-center" style={{ background: "var(--navy-deep)", border: "1px dashed rgba(255,255,255,0.12)" }} data-testid="reels-empty-state">
                    <div className="text-white/65 mb-4">{t("reels.empty")}</div>
                    <button onClick={() => setShowCreate(true)} className="btn-gold text-sm !py-2.5 !px-5" data-testid="reels-empty-create">
                        <Plus size={14} weight="bold" /> {t("reels.newReel")}
                    </button>
                </div>
            )}

            {/* Responsive Thumbnail Grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="reels-stage">
                {reels.map((reel) => (
                    <ReelThumbnail
                        key={reel.id}
                        reel={reel}
                        user={user}
                        onOpen={() => setActiveReel(reel)}
                        onLike={() => handleLike(reel)}
                        onSave={() => handleSave(reel)}
                    />
                ))}
            </div>
            {(loading || loadingMore) && (
                <div className="text-center text-white/50 py-6 text-sm">Loading reels…</div>
            )}
            <div ref={sentinelRef} className="h-4" />

            {/* 3-Column Modal Viewer */}
            <AnimatePresence>
                {activeReel && (
                    <ReelModal
                        reel={activeReel}
                        user={user}
                        onClose={() => setActiveReel(null)}
                        onLike={() => handleLike(activeReel)}
                        onSave={() => handleSave(activeReel)}
                        onShare={() => handleShareInMessages(activeReel)}
                        onOpenComments={() => setCommentReel(activeReel)}
                        onReport={() => setReportReel(activeReel)}
                        onPrev={() => {
                            const index = reels.findIndex(r => r.id === activeReel.id);
                            if (index > 0) setActiveReel(reels[index - 1]);
                        }}
                        onNext={() => {
                            const index = reels.findIndex(r => r.id === activeReel.id);
                            if (index < reels.length - 1) setActiveReel(reels[index + 1]);
                        }}
                    />
                )}
            </AnimatePresence>

            <ReelComposerModal
                open={showCreate}
                onClose={() => setShowCreate(false)}
                onCreated={(reel) => {
                    setShowCreate(false);
                    setMode("for_you");
                    setReels((prev) => [reel, ...prev.filter((item) => item.id !== reel.id)]);
                    setActiveReel(reel);
                    onReelCreated?.();
                }}
            />
            <ReelCommentsDrawer
                reel={commentReel}
                open={!!commentReel}
                onClose={() => setCommentReel(null)}
                onCountChange={(count) => commentReel && updateReel(commentReel.id, { comments_count: count })}
            />
            <ReportModal open={!!reportReel} onClose={() => setReportReel(null)} targetType="reel" targetId={reportReel?.id} />
        </div>
    );
}

function ReelThumbnail({ reel, user, onOpen, onLike, onSave }) {
    const [muted, setMuted] = useState(true);
    const videoRef = useRef(null);

    const handleMouseEnter = () => videoRef.current?.play().catch(() => {});
    const handleMouseLeave = () => { videoRef.current?.pause(); if (videoRef.current) videoRef.current.currentTime = 0; };

    return (
        <div
            className="relative rounded-2xl overflow-hidden cursor-pointer group"
            style={{ aspectRatio: "9/16", background: "#04070d" }}
            onClick={onOpen}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            data-testid={`reel-thumb-${reel.id}`}
        >
            {/* Poster / video */}
            <video
                ref={videoRef}
                src={toAbsolute(reel.media_url)}
                poster={toAbsolute(reel.poster_url) || undefined}
                muted={muted}
                loop
                playsInline
                preload="metadata"
                className="absolute inset-0 w-full h-full object-cover"
            />
            {/* Gradient overlay */}
            <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(3,7,15,0.85) 0%, rgba(3,7,15,0.1) 50%, transparent 100%)" }} />
            {/* Bottom overlay: avatar, friend/follow, mute */}
            <div className="absolute bottom-0 left-0 right-0 p-3 flex items-end justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                    <div className="relative shrink-0 mb-1">
                        <img
                            src={reel.user_avatar || `https://api.dicebear.com/7.x/notionists/svg?seed=${reel.user_handle}`}
                            alt=""
                            className="w-8 h-8 rounded-full object-cover"
                            style={{ border: "1px solid rgba(212,175,55,0.45)" }}
                        />
                        {user?.id !== reel.user_id && (
                            <FollowButton handle={reel.user_handle} variant="overlay" className="scale-75 origin-bottom" />
                        )}
                    </div>
                    <div className="min-w-0">
                        <div className="text-white text-xs font-medium truncate">@{reel.user_handle}</div>
                        {(reel.location || reel.country) && (
                            <div className="text-white/60 text-[10px] flex items-center gap-1 truncate">
                                <MapPin size={9} color="#D4AF37" />{reel.location || reel.country}
                            </div>
                        )}
                    </div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0" onClick={(e) => e.stopPropagation()}>
                    <button
                        onClick={(e) => { e.stopPropagation(); setMuted((m) => !m); }}
                        className="w-7 h-7 rounded-full grid place-items-center"
                        style={{ background: "rgba(0,0,0,0.55)" }}
                    >
                        {muted ? <SpeakerSlash size={12} color="white" /> : <SpeakerHigh size={12} color="#D4AF37" />}
                    </button>
                </div>
            </div>
            {/* Like & View count badges */}
            <div className="absolute top-2 right-2 flex items-center gap-1.5">
                <div className="flex items-center gap-1 px-2 py-1 rounded-full text-[10px] text-white" style={{ background: "rgba(0,0,0,0.55)" }}>
                    <Eye size={10} color="white" />
                    {reel.views || 0}
                </div>
                <div className="flex items-center gap-1 px-2 py-1 rounded-full text-[10px] text-white" style={{ background: "rgba(0,0,0,0.55)" }}>
                    <Heart size={10} weight={reel.liked ? "fill" : "regular"} color={reel.liked ? "#ff7070" : "white"} />
                    {reel.likes || 0}
                </div>
            </div>
        </div>
    );
}



function ReelViewport({ reel, index, active, nearActive, muted, onToggleMute, onAttachRef, onLike, onSave, onShare, onOpenComments, onReport, onDelete, onEdit, onNotInterested, onCommentCountChange }) {
    const { user } = useAuth() || {};
    const [paused, setPaused] = useState(false);
    const [errored, setErrored] = useState(false);
    const [related, setRelated] = useState([]);
    const videoRef = useRef(null);

    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;
        if (active && !paused) {
            video.play().catch(() => {});
        } else {
            video.pause();
        }
    }, [active, paused, reel.id]);

    useEffect(() => {
        if (!active) return;
        reelsApi.related(reel.id).then((items) => setRelated(items || [])).catch(() => setRelated([]));
    }, [active, reel.id]);

    const togglePlayback = () => {
        const video = videoRef.current;
        if (!video) return;
        if (video.paused) {
            video.play().catch(() => {});
            setPaused(false);
        } else {
            video.pause();
            setPaused(true);
        }
    };

    return (
        <section
            ref={onAttachRef}
            data-index={index}
            className="snap-start rounded-[32px] overflow-hidden relative min-h-[calc(100vh-275px)]"
            style={{ background: "#04070d", border: "1px solid rgba(255,255,255,0.08)" }}
            data-testid={`reel-card-${reel.id}`}
        >
            <div className="absolute inset-0">
                {!errored ? (
                    <video
                        ref={videoRef}
                        src={toAbsolute(reel.media_url)}
                        poster={toAbsolute(reel.poster_url) || undefined}
                        loop
                        playsInline
                        muted={muted}
                        preload={nearActive ? "auto" : "metadata"}
                        className="w-full h-full object-cover"
                        onClick={togglePlayback}
                        onError={() => setErrored(true)}
                        onEnded={() => reelsApi.view(reel.id, { session_id: getSessionId(), completed: true, source: "playback" }).catch(() => {})}
                        data-testid={`reel-video-${reel.id}`}
                    />
                ) : (
                    <div className="w-full h-full grid place-items-center text-white/60 px-6 text-center" data-testid={`reel-video-fallback-${reel.id}`}>
                        This reel couldn&rsquo;t load. Upload a fresh clip to keep the feed moving.
                    </div>
                )}
                <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(3,7,15,0.28) 0%, rgba(3,7,15,0.08) 24%, rgba(3,7,15,0.22) 58%, rgba(3,7,15,0.9) 100%)" }} />
            </div>

            <div className="relative z-10 flex flex-col justify-between min-h-[calc(100vh-275px)] p-5 md:p-6">
                <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                        <Link to={`/profile/${reel.user_handle}`} className="shrink-0" data-testid={`reel-profile-link-${reel.id}`}>
                            <img src={reel.user_avatar || `https://api.dicebear.com/7.x/notionists/svg?seed=${reel.user_handle}`} alt="" className="w-11 h-11 rounded-full object-cover" style={{ border: "1px solid rgba(212,175,55,0.45)" }} />
                        </Link>
                        <div className="min-w-0">
                            <Link to={`/profile/${reel.user_handle}`} className="font-medium text-white truncate block" data-testid={`reel-handle-${reel.id}`}>@{reel.user_handle}</Link>
                            <div className="text-white/60 text-xs truncate" data-testid={`reel-meta-${reel.id}`}>{reel.location || reel.destination || reel.country || "Passport"} · {timeAgo(reel.created_at)}</div>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        {user?.id !== reel.user_id && <FollowButton handle={reel.user_handle} className="backdrop-blur-sm bg-black/25" />}
                        <button onClick={onToggleMute} className="w-10 h-10 rounded-full grid place-items-center bg-black/30 border border-white/10" data-testid={`reel-mute-${reel.id}`} aria-label="Toggle sound">
                            {muted ? <SpeakerSlash size={14} /> : <SpeakerHigh size={14} color="#D4AF37" />}
                        </button>
                        <button onClick={togglePlayback} className="w-10 h-10 rounded-full grid place-items-center bg-black/30 border border-white/10" data-testid={`reel-play-toggle-${reel.id}`} aria-label="Toggle playback">
                            {paused ? <Play size={14} weight="fill" /> : <Pause size={14} weight="fill" />}
                        </button>
                        <ReelOptionsMenu reel={reel} isOwner={user?.id === reel.user_id} onEdit={onEdit} onDelete={onDelete} onReport={onReport} onNotInterested={onNotInterested} onSend={handleShareInMessages} onShareLink={handleShare} />
                    </div>
                </div>

                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-5">
                    <div className="max-w-2xl">
                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[10px] uppercase tracking-[0.18em] mb-3" style={{ background: "rgba(212,175,55,0.15)", color: "var(--gold)" }} data-testid={`reel-reason-${reel.id}`}>
                            {reel.recommendation_reason}
                        </div>
                        <div className="font-serif text-xl md:text-2xl text-white leading-tight mb-3" data-testid={`reel-caption-${reel.id}`}>{reel.caption}</div>
                        <div className="flex flex-wrap gap-2 mb-3">
                            {(reel.location || reel.destination || reel.country) && (
                                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs" style={{ background: "rgba(255,255,255,0.08)", color: "white" }} data-testid={`reel-atlas-link-${reel.id}`}>
                                    <MapPin size={12} color="#D4AF37" /> {reel.location || reel.destination || reel.country}
                                </span>
                            )}
                            {reel.atlas_context?.visited_by_viewer && (
                                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs" style={{ background: "rgba(159,213,208,0.14)", color: "#9FD5D0" }} data-testid={`reel-atlas-status-${reel.id}`}>
                                    Already in your Atlas
                                </span>
                            )}
                            {(reel.destination_tags || []).slice(0, 4).map((tag) => (
                                <span key={tag} className="px-3 py-1.5 rounded-full text-xs" style={{ background: "rgba(255,255,255,0.06)", color: "rgba(255,255,255,0.72)" }} data-testid={`reel-tag-${reel.id}-${tag}`}>
                                    #{tag.replace(/-/g, " ")}
                                </span>
                            ))}
                        </div>
                        {related.length > 0 && (
                            <div className="flex flex-wrap gap-2 text-xs text-white/70" data-testid={`reel-related-${reel.id}`}>
                                <span className="text-white/45 uppercase tracking-[0.18em]">Up next</span>
                                {related.slice(0, 3).map((item) => (
                                    <Link key={item.id} to={`/app?tab=reels&reel=${item.id}`} className="px-3 py-1 rounded-full bg-black/25 border border-white/10" data-testid={`reel-related-link-${reel.id}-${item.id}`}>
                                        {item.location || item.country || item.user_handle}
                                    </Link>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="flex md:flex-col gap-3 md:items-end">
                        <div className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full bg-black/32 border border-white/10 text-sm text-white min-w-[92px]" data-testid={`reel-views-${reel.id}`}>
                            <Eye size={18} />
                            <span>{reel.views || 0}</span>
                        </div>
                        <ActionButton icon={<Heart size={18} weight={reel.liked ? "fill" : "regular"} color={reel.liked ? "#ff7070" : "white"} />} label={reel.likes || 0} onClick={onLike} testId={`reel-like-${reel.id}`} />
                        <ActionButton icon={<ChatCircle size={18} />} label={reel.comments_count || 0} onClick={onOpenComments} testId={`reel-comments-${reel.id}`} />
                        <ActionButton icon={<PaperPlaneTilt size={18} />} label={reel.shares || 0} onClick={onShare} testId={`reel-share-${reel.id}`} />
                        <ActionButton icon={<BookmarkSimple size={18} weight={reel.saved ? "fill" : "regular"} color={reel.saved ? "#D4AF37" : "white"} />} label={reel.saves || 0} onClick={onSave} testId={`reel-save-${reel.id}`} />
                    </div>
                </div>
            </div>
        </section>
    );
}

function ActionButton({ icon, label, onClick, testId }) {
    return (
        <button onClick={onClick} className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full bg-black/32 border border-white/10 text-sm text-white min-w-[92px]" data-testid={testId}>
            {icon}
            <span data-testid={`${testId}-count`}>{label}</span>
        </button>
    );
}

function ReelComposerModal({ open, onClose, onCreated }) {
    const { t } = useTranslation();
    const [media, setMedia] = useState(null);
    const [caption, setCaption] = useState("");
    const [location, setLocation] = useState("");
    const [country, setCountry] = useState("");
    const [destination, setDestination] = useState("");
    const [destinationTags, setDestinationTags] = useState("");
    const [privacy, setPrivacy] = useState("public");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!open) return;
        setBusy(false);
    }, [open]);

    const submit = async (event) => {
        event?.preventDefault?.();
        if (!media?.url) return toast.error(t("reels.toasts.addVideo"));
        if (media.kind !== "video") return toast.error(t("reels.toasts.videoOnly"));
        if (!location && !country && !destination) return toast.error("Please provide a location, country, or destination.");
        setBusy(true);
        try {
            const reel = await reelsApi.create({
                media_url: media.url,
                media_id: media.id || null,
                poster_url: media.poster_url || null,
                caption,
                location: location || null,
                country: country || null,
                destination: destination || location || country || null,
                destination_tags: destinationTags.split(",").map((item) => item.trim()).filter(Boolean),
                duration_seconds: media.duration_seconds || null,
                privacy,
            });
            setMedia(null);
            setCaption("");
            setLocation("");
            setCountry("");
            setDestination("");
            setDestinationTags("");
            setPrivacy("public");
            toast.success(t("reels.toasts.posted"));
            onCreated?.(reel);
        } catch {
            toast.error(t("reels.toasts.failed"));
        } finally {
            setBusy(false);
        }
    };

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-[92] flex items-center justify-center p-4"
                    style={{ background: "rgba(5,10,17,0.84)", backdropFilter: "blur(10px)" }}
                    onClick={onClose}
                    data-testid="reel-create-modal"
                >
                    <motion.form
                        initial={{ y: 24, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: 20, opacity: 0 }}
                        onClick={(event) => event.stopPropagation()}
                        onSubmit={submit}
                        className="w-full max-w-2xl rounded-[32px] p-8 max-h-[92vh] overflow-y-auto"
                        style={{ background: "var(--navy)", border: "1px solid rgba(212,175,55,0.28)" }}
                    >
                        <div className="flex items-start justify-between gap-4 mb-6">
                            <div>
                                <div className="overline mb-2">{t("reels.creator.overline")}</div>
                                <h3 className="font-serif text-3xl text-white" data-testid="reel-create-title">{t("reels.creator.title")}</h3>
                                <p className="text-white/55 text-sm mt-2 max-w-lg">Upload a vertical travel clip, tag the destination, and drop it straight into Passport Reels.</p>
                            </div>
                            <button type="button" onClick={onClose} className="w-10 h-10 rounded-full glass grid place-items-center" data-testid="reel-create-close">×</button>
                        </div>

                        <div className="grid lg:grid-cols-[1.1fr_0.9fr] gap-6">
                            <MediaUploader value={media} onChange={setMedia} label={t("reels.creator.mediaLabel")} accept="video" maxDuration={60} testId="reel-media" height={360} />

                            <div className="space-y-4">
                                <textarea value={caption} onChange={(event) => setCaption(event.target.value)} rows={5} placeholder={t("reels.creator.placeholders.caption")} className="input" data-testid="reel-caption" />
                                <input value={location} onChange={(event) => setLocation(event.target.value)} placeholder={t("reels.creator.placeholders.location")} className="input" data-testid="reel-location" />
                                <input value={country} onChange={(event) => setCountry(event.target.value)} placeholder={t("reels.creator.placeholders.country")} className="input" data-testid="reel-country" />
                                <input value={destination} onChange={(event) => setDestination(event.target.value)} placeholder="Destination headline (optional)" className="input" data-testid="reel-destination" />
                                <input value={destinationTags} onChange={(event) => setDestinationTags(event.target.value)} placeholder="Destination tags, comma separated" className="input" data-testid="reel-destination-tags" />
                                <div className="space-y-2">
                                    <div className="text-xs uppercase tracking-[0.24em] text-white/45">Privacy</div>
                                    <div className="grid grid-cols-2 gap-2" data-testid="reel-privacy-options">
                                        {[
                                            { key: "public", label: "Public" },
                                            { key: "followers", label: "Followers" },
                                            { key: "friends", label: "Friends" },
                                            { key: "private", label: "Private" },
                                        ].map((item) => (
                                            <button
                                                key={item.key}
                                                type="button"
                                                onClick={() => setPrivacy(item.key)}
                                                className="px-4 py-3 rounded-2xl text-sm transition-colors"
                                                style={{ background: privacy === item.key ? "var(--gold)" : "rgba(255,255,255,0.05)", color: privacy === item.key ? "var(--navy)" : "white" }}
                                                data-testid={`reel-privacy-${item.key}`}
                                            >
                                                {item.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="flex gap-3 mt-6">
                            <button type="button" onClick={onClose} className="btn-ghost flex-1 justify-center" data-testid="reel-cancel">Cancel</button>
                            <button type="button" onClick={submit} disabled={busy} className="btn-gold flex-1 justify-center" data-testid="reel-save">
                                {busy ? t("reels.creator.posting") : t("reels.creator.postReel")}
                            </button>
                        </div>

                        <style>{`.input { width: 100%; background: rgba(0,0,0,0.28); border: 1px solid rgba(255,255,255,0.08); border-radius: 18px; padding: 12px 14px; color: white; font-size: 14px; } .input::placeholder { color: rgba(255,255,255,0.34); } .input:focus { outline: none; border-color: var(--gold); }`}</style>
                    </motion.form>
                </motion.div>
            )}
        </AnimatePresence>
    );
}